import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  ListItemIcon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import { useTranslation } from 'react-i18next';
import { socialLinks } from '../../config/socialLinks';
import LocaleSwitcher from '../LocaleSwitcher';
import DiscordIcon from './DiscordIcon';

const AccountMenu = ({ onLogout, user }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const avatarAlt = user?.name || t('account.profile');
  const canAccessAdminTools = user?.role === 'tavern_master';

  const closeMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    closeMenu();
    onLogout();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleScroll = () => setAnchorEl(null);
    const listenerOptions = { capture: true, passive: true };

    window.addEventListener('scroll', handleScroll, listenerOptions);
    window.addEventListener('touchmove', handleScroll, listenerOptions);
    window.addEventListener('wheel', handleScroll, listenerOptions);
    document.addEventListener('scroll', handleScroll, listenerOptions);
    document.addEventListener('touchmove', handleScroll, listenerOptions);

    return () => {
      window.removeEventListener('scroll', handleScroll, listenerOptions);
      window.removeEventListener('touchmove', handleScroll, listenerOptions);
      window.removeEventListener('wheel', handleScroll, listenerOptions);
      document.removeEventListener('scroll', handleScroll, listenerOptions);
      document.removeEventListener('touchmove', handleScroll, listenerOptions);
    };
  }, [open]);

  return (
    <>
      <IconButton
        aria-label={t('account.openMenu')}
        aria-controls={open ? 'account-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="menu"
        onClick={(event) => {
          setAnchorEl((current) => (current ? null : event.currentTarget));
        }}
        sx={{ p: 0 }}
      >
        <Avatar
          src={user?.avatar || undefined}
          alt={avatarAlt}
          sx={{
            width: 46,
            height: 46,
            border: '2px solid',
            borderColor: open ? 'primary.main' : 'divider',
            boxShadow: 1,
          }}
        />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        modifiers={[
          { name: 'offset', options: { offset: [0, 10] } },
          { name: 'preventOverflow', options: { padding: 16 } },
        ]}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={closeMenu}>
          <Paper
            elevation={0}
            sx={{
              width: 304,
              maxWidth: 'calc(100vw - 32px)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 18px 42px -28px rgba(6, 37, 34, 0.42)',
            }}
          >
            <MenuList
              id="account-menu"
              autoFocusItem={open}
              onKeyDown={(event) => {
                if (event.key === 'Escape' || event.key === 'Tab') {
                  closeMenu();
                }
              }}
            >
              <MenuItem
                component={RouterLink}
                to="/profile"
                onClick={closeMenu}
                sx={{
                  px: 2,
                  py: 1.5,
                  whiteSpace: 'normal',
                  cursor: 'pointer',
                  transition: (theme) =>
                    theme.transitions.create(['background-color', 'box-shadow', 'opacity'], {
                      duration: theme.transitions.duration.shortest,
                    }),
                  '&:hover': {
                    bgcolor: 'rgba(215, 160, 75, 0.12)',
                    boxShadow: 'inset 3px 0 0 rgba(215, 160, 75, 0.78)',
                  },
                  '&:active': {
                    opacity: 0.82,
                    bgcolor: 'rgba(15, 76, 69, 0.1)',
                  },
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar src={user?.avatar || undefined} alt={avatarAlt} sx={{ width: 44, height: 44 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('account.signedInAs')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Cinzel", Georgia, serif',
                        fontWeight: 800,
                        lineHeight: 1.1,
                      }}
                      noWrap
                    >
                      {user ? `${user.title} ${user.name}`.trim() : t('account.profile')}
                    </Typography>
                  </Box>
                </Stack>
              </MenuItem>

              <Divider />

              <MenuItem component={RouterLink} to="/profile" onClick={closeMenu}>
                <ListItemIcon>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                {t('account.profile')}
              </MenuItem>
              <MenuItem component={RouterLink} to="/settings" onClick={closeMenu}>
                <ListItemIcon>
                  <SettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('settings.title')}
              </MenuItem>
              <MenuItem component={RouterLink} to="/support" onClick={closeMenu}>
                <ListItemIcon>
                  <SupportAgentOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t('account.support')}
              </MenuItem>
              {canAccessAdminTools ? (
                <MenuItem component={RouterLink} to="/settings/tavern-master-tools" onClick={closeMenu}>
                  <ListItemIcon>
                    <AdminPanelSettingsOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {t('adminTools.menuLabel')}
                </MenuItem>
              ) : null}
              <MenuItem
                component="a"
                href={socialLinks.discord}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
              >
                <ListItemIcon>
                  <DiscordIcon fontSize="small" />
                </ListItemIcon>
                {t('social.discord.menuLabel')}
              </MenuItem>

              <Divider />

              <Box sx={{ px: 2, py: 1.5 }} onClick={(event) => event.stopPropagation()}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LanguageIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('account.language')}
                    </Typography>
                  </Stack>
                  <LocaleSwitcher fullWidth />
                </Stack>
              </Box>

              <Divider />

              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <LogoutIcon color="error" fontSize="small" />
                </ListItemIcon>
                {t('login.logout')}
              </MenuItem>
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AccountMenu;
